import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Mail, PackageCheck, Pencil, Plus, RotateCcw, Trash2, Upload, X } from "lucide-react";
import { api, formatApiError, img, money } from "@/lib/api";
import { useStore } from "@/store/StoreContext";
import { Eyebrow, Reveal } from "@/components/Reveal";

const CATEGORIES = ["Tees", "Hoodies", "Outerwear", "Polos", "Performance", "Sets", "Accessories"];
const STATUSES = ["available", "limited", "sold_out", "coming_soon", "restock"];
const EMPTY = { name: "", category: "Tees", price: "", status: "available", badge: "", story: "",
  gsm: "", material: "", fit: "", care: "", sizes: "XS,S,M,L,XL,XXL", units: 0, featured: false, drop: false, images: [] };

const inputCls = "w-full bg-transparent border border-white/15 px-4 py-3 text-sm placeholder:text-cream/30 focus:outline-none focus:border-gold/60 transition-colors";
const labelCls = "font-mono2 text-[9px] tracking-[0.25em] uppercase text-cream/40 block mb-2";

function ProductForm({ initial, images, isNew, onSave, onCancel }) {
  const [f, setF] = useState({
    ...EMPTY, ...initial,
    sizes: Array.isArray(initial.sizes) ? initial.sizes.join(",") : (initial.sizes || ""),
    price: initial.price ?? "",
    size_stock: initial.size_stock || {},
  });
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const currentImage = f.image || (initial.images || [])[0] || "";

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/images/upload", fd);
      set("image", data.path);
      toast.success("Photo uploaded.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await onSave({
        name: f.name, category: f.category, price: parseFloat(f.price) || 0,
        status: f.status, badge: f.badge || null, story: f.story, gsm: f.gsm,
        material: f.material, fit: f.fit, care: f.care,
        sizes: f.sizes.split(",").map((s) => s.trim()).filter(Boolean),
        units: parseInt(f.units) || 0, featured: f.featured, drop: f.drop,
        size_stock: f.size_stock && Object.keys(f.size_stock).length ? f.size_stock : undefined,
        images: f.image ? [f.image, "fabric-detail.jpg"] : (initial.images || []),
      });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="border border-gold/30 p-6 sm:p-8 mb-10" data-testid="admin-product-form">
      <div className="flex justify-between items-center mb-6">
        <h3 className="font-display font-bold uppercase tracking-wide">{isNew ? "New Product" : `Edit — ${initial.name}`}</h3>
        <button type="button" onClick={onCancel} data-testid="admin-form-cancel" aria-label="Cancel"><X size={18} className="text-cream/50 hover:text-gold" /></button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div><label className={labelCls}>Name</label><input required value={f.name} onChange={(e) => set("name", e.target.value)} className={inputCls} data-testid="admin-field-name" /></div>
        <div><label className={labelCls}>Price (USD)</label><input required type="number" min="0" step="1" value={f.price} onChange={(e) => set("price", e.target.value)} className={inputCls} data-testid="admin-field-price" /></div>
        <div>
          <label className={labelCls}>Category</label>
          <select value={f.category} onChange={(e) => set("category", e.target.value)} className={`${inputCls} bg-noir-bg`} data-testid="admin-field-category">
            {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <label className={labelCls}>Status</label>
          <select value={f.status} onChange={(e) => set("status", e.target.value)} className={`${inputCls} bg-noir-bg`} data-testid="admin-field-status">
            {STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
          </select>
        </div>
        <div><label className={labelCls}>Badge (optional)</label><input value={f.badge || ""} onChange={(e) => set("badge", e.target.value)} placeholder="Limited Drop" className={inputCls} data-testid="admin-field-badge" /></div>
        <div><label className={labelCls}>Total Units</label><p className="px-4 py-3 text-sm text-gold font-mono2 border border-white/10" data-testid="admin-total-units">{Object.values(f.size_stock || {}).reduce((a, b) => a + (parseInt(b) || 0), 0) || f.units || 0} (auto from size stock)</p></div>
        <div className="sm:col-span-2"><label className={labelCls}>Story</label><textarea rows={3} value={f.story} onChange={(e) => set("story", e.target.value)} className={`${inputCls} resize-none`} data-testid="admin-field-story" /></div>
        <div><label className={labelCls}>Material / GSM</label><input value={f.gsm} onChange={(e) => set("gsm", e.target.value)} placeholder="240 GSM" className={inputCls} data-testid="admin-field-gsm" /></div>
        <div><label className={labelCls}>Sizes (comma separated)</label><input value={f.sizes} onChange={(e) => set("sizes", e.target.value)} className={inputCls} data-testid="admin-field-sizes" /></div>
        <div className="sm:col-span-2">
          <label className={labelCls}>Stock per Size</label>
          <div className="flex flex-wrap gap-2" data-testid="admin-size-stock-grid">
            {f.sizes.split(",").map((s) => s.trim()).filter(Boolean).map((s) => (
              <div key={s} className="border border-white/15 px-3 py-2 flex items-center gap-2">
                <span className="font-mono2 text-xs text-cream/60 w-8">{s}</span>
                <input
                  type="number"
                  min="0"
                  value={(f.size_stock || {})[s] ?? 0}
                  onChange={(e) => set("size_stock", { ...(f.size_stock || {}), [s]: Math.max(0, parseInt(e.target.value) || 0) })}
                  className="w-16 bg-transparent border border-white/10 px-2 py-1 text-sm text-gold font-mono2 focus:outline-none focus:border-gold/60"
                  data-testid={`admin-stock-${s.toLowerCase()}`}
                />
              </div>
            ))}
          </div>
          <p className="text-[10px] text-cream/30 mt-2 font-mono2 tracking-wide">A size at 0 shows "Sold Out" on that size button. All sizes at 0 sells out the piece.</p>
        </div>
        <div><label className={labelCls}>Material detail</label><input value={f.material} onChange={(e) => set("material", e.target.value)} className={inputCls} data-testid="admin-field-material" /></div>
        <div><label className={labelCls}>Fit</label><input value={f.fit} onChange={(e) => set("fit", e.target.value)} className={inputCls} data-testid="admin-field-fit" /></div>
        <div className="sm:col-span-2"><label className={labelCls}>Care</label><input value={f.care} onChange={(e) => set("care", e.target.value)} className={inputCls} data-testid="admin-field-care" /></div>

        <div className="sm:col-span-2">
          <label className={labelCls}>Product Photo</label>
          <div className="flex flex-col sm:flex-row gap-4 sm:items-end">
            {currentImage && (
              <div className="w-20 aspect-[3/4] bg-noir-card overflow-hidden border border-white/10 shrink-0" data-testid="admin-image-preview">
                <img src={img(currentImage)} alt="" className="w-full h-full object-cover" />
              </div>
            )}
            <div className="flex-1">
              <select value={currentImage} onChange={(e) => set("image", e.target.value)} className={`${inputCls} bg-noir-bg`} data-testid="admin-field-image">
                <option value="">Choose existing image…</option>
                {images.map((im) => <option key={im} value={im}>{im}</option>)}
              </select>
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                className="mt-2 inline-flex items-center gap-2 border border-gold/50 text-gold px-4 py-2.5 font-mono2 text-[9px] tracking-[0.2em] uppercase hover:bg-gold hover:text-noir-bg transition-colors disabled:opacity-50"
                data-testid="admin-upload-photo-button"
              >
                <Upload size={12} /> {uploading ? "Uploading…" : "Upload New Photo"}
              </button>
              <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={uploadImage} data-testid="admin-upload-photo-input" />
            </div>
          </div>
        </div>

        <div className="sm:col-span-2 flex items-center gap-6">
          <label className="flex items-center gap-2 text-sm text-cream/60 cursor-pointer">
            <input type="checkbox" checked={f.featured} onChange={(e) => set("featured", e.target.checked)} className="accent-[#D4AF37]" data-testid="admin-field-featured" /> Featured
          </label>
          <label className="flex items-center gap-2 text-sm text-cream/60 cursor-pointer">
            <input type="checkbox" checked={f.drop} onChange={(e) => set("drop", e.target.checked)} className="accent-[#D4AF37]" data-testid="admin-field-drop" /> New Drop
          </label>
        </div>
      </div>
      <button type="submit" disabled={busy} className="mt-6 bg-gold text-noir-bg px-8 py-3.5 font-mono2 text-[10px] tracking-[0.25em] uppercase hover:bg-cream transition-colors disabled:opacity-50" data-testid="admin-save-button">
        {busy ? "Saving…" : "Save Product"}
      </button>
    </form>
  );
}

function ShipForm({ order, onDone }) {
  const [carrier, setCarrier] = useState("UPS");
  const [tracking, setTracking] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.put(`/admin/orders/${order.order_id}/ship`, { carrier, tracking_number: tracking });
      toast.success(data.emailed ? "Marked shipped — customer emailed." : "Marked shipped — no customer email on file.");
      onDone();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="mt-4 pt-4 border-t border-white/10 flex flex-wrap gap-3 items-end" data-testid={`ship-form-${order.order_id}`}>
      <div>
        <label className={labelCls}>Carrier</label>
        <select value={carrier} onChange={(e) => setCarrier(e.target.value)} className={`${inputCls} bg-noir-bg`} data-testid={`ship-carrier-${order.order_id}`}>
          {["UPS", "USPS", "FedEx", "DHL"].map((c) => <option key={c}>{c}</option>)}
        </select>
      </div>
      <div className="flex-1 min-w-[180px]">
        <label className={labelCls}>Tracking Number</label>
        <input required value={tracking} onChange={(e) => setTracking(e.target.value)} placeholder="1Z999AA10123456784" className={inputCls} data-testid={`ship-tracking-${order.order_id}`} />
      </div>
      <button type="submit" disabled={busy} className="bg-gold text-noir-bg px-6 py-3 font-mono2 text-[10px] tracking-[0.2em] uppercase hover:bg-cream transition-colors disabled:opacity-50" data-testid={`ship-submit-${order.order_id}`}>
        {busy ? "Sending…" : "Ship + Email Customer"}
      </button>
    </form>
  );
}

function MessageCard({ msg, onDone }) {
  const [open, setOpen] = useState(false);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/admin/messages/${msg.id}/reply`, { reply });
      toast.success(`Reply emailed to ${msg.email}.`);
      setOpen(false);
      onDone();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`/admin/messages/${msg.id}`);
      toast.success("Message deleted.");
      onDone();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setBusy(false);
    }
  };

  return (
    <div className="border border-white/10 p-6" data-testid={`admin-message-${msg.id}`}>
      <div className="flex flex-wrap justify-between gap-3 mb-3 items-center">
        <span className="font-display text-sm font-bold uppercase">{msg.name}</span>
        <span className="text-sm text-cream/60">{msg.email}</span>
        <div className="flex items-center gap-3">
          {msg.replied_at && (
            <span className="font-mono2 text-[9px] tracking-[0.2em] uppercase px-2 py-1 border bg-cream text-noir-bg border-cream" data-testid={`replied-badge-${msg.id}`}>Replied</span>
          )}
          <span className="text-cream/30 text-xs">
            {new Date(msg.sent_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
          </span>
          <button onClick={remove} disabled={busy} data-testid={`delete-message-${msg.id}`} aria-label="Delete message">
            <Trash2 size={13} className="text-cream/40 hover:text-red-400 transition-colors" />
          </button>
        </div>
      </div>
      <p className="text-sm text-cream/70 leading-relaxed">{msg.message}</p>
      {open ? (
        <form onSubmit={submit} className="mt-4 pt-4 border-t border-white/10" data-testid={`reply-form-${msg.id}`}>
          <label className={labelCls}>Reply — sent to {msg.email} as Jae Noir</label>
          <textarea required rows={4} value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Write your reply…" className={inputCls} data-testid={`reply-input-${msg.id}`} />
          <div className="flex gap-3 mt-3">
            <button type="submit" disabled={busy} className="bg-gold text-noir-bg px-6 py-3 font-mono2 text-[10px] tracking-[0.2em] uppercase hover:bg-cream transition-colors disabled:opacity-50" data-testid={`reply-send-${msg.id}`}>
              {busy ? "Sending…" : "Send Reply"}
            </button>
            <button type="button" onClick={() => setOpen(false)} className="px-6 py-3 font-mono2 text-[10px] tracking-[0.2em] uppercase text-cream/50 hover:text-gold transition-colors" data-testid={`reply-cancel-${msg.id}`}>
              Cancel
            </button>
          </div>
        </form>
      ) : (
        <button onClick={() => setOpen(true)} className="mt-4 inline-flex items-center gap-2 border border-gold/40 text-gold px-4 py-2 font-mono2 text-[9px] tracking-[0.2em] uppercase hover:bg-gold hover:text-noir-bg transition-colors" data-testid={`reply-open-${msg.id}`}>
          <Mail size={12} /> Reply
        </button>
      )}
    </div>
  );
}


export default function Admin() {
  const { user } = useStore();
  const navigate = useNavigate();
  const [tab, setTab] = useState("orders");
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [images, setImages] = useState([]);
  const [messages, setMessages] = useState([]);
  const [editing, setEditing] = useState(null);
  const [shipOpen, setShipOpen] = useState(null);

  const loadAll = () => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/orders").then((r) => setOrders(r.data)).catch(() => {});
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/admin/images").then((r) => setImages(r.data)).catch(() => {});
    api.get("/admin/messages").then((r) => setMessages(r.data)).catch(() => {});
  };

  useEffect(() => { if (user === false) navigate("/account"); }, [user, navigate]);
  useEffect(() => { if (user && user.role === "admin") loadAll(); }, [user]);

  if (!user || user.role !== "admin") {
    return (
      <div className="pt-16 min-h-screen flex items-center justify-center" data-testid="admin-denied">
        <p className="font-mono2 text-xs tracking-[0.3em] uppercase text-cream/40">
          {user === null ? "Loading…" : "Founders Circle only."}
        </p>
      </div>
    );
  }

  const saveProduct = async (payload) => {
    if (editing === "new") await api.post("/admin/products", payload);
    else await api.put(`/admin/products/${editing.slug}`, payload);
    toast.success("Product saved.");
    setEditing(null);
    loadAll();
  };

  const restock = async (slug) => {
    try {
      const { data } = await api.post(`/admin/products/${slug}/restock`);
      toast.success(`Restock live — ${data.emailed} emailed, ${data.texted} texted.`);
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const unread = messages.filter((m) => !m.replied_at).length;

  return (
    <div className="pt-16" data-testid="admin-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 sm:pt-20 pb-24">
        <Reveal><Eyebrow>Founders Circle</Eyebrow></Reveal>
        <Reveal delay={0.1}>
          <h1 className="font-display font-extrabold uppercase tracking-tight text-4xl sm:text-6xl mt-4">Command <span className="text-outline">Room</span></h1>
        </Reveal>

        {stats && (
          <Reveal delay={0.15}>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-10" data-testid="admin-stats">
              {[["Revenue", money(stats.revenue), "revenue"], ["Orders", stats.orders, "orders"], ["Products", stats.products, "products"], ["Noir List", stats.noir_list, "noir-list"], ["Alerts Armed", stats.restock_alerts, "alerts"]].map(([label, val, id]) => (
                <div key={id} className="border border-white/10 p-5" data-testid={`admin-stat-${id}`}>
                  <p className="font-mono2 text-[9px] tracking-[0.25em] uppercase text-cream/40">{label}</p>
                  <p className="font-display font-bold text-2xl text-gold mt-2">{val}</p>
                </div>
              ))}
            </div>
          </Reveal>
        )}

        <div className="flex gap-1 mt-10 border border-white/10 p-1 w-fit" data-testid="admin-tabs">
          {["orders", "products", "inbox"].map((t) => (
            <button key={t} onClick={() => setTab(t)} className={`px-5 py-2.5 font-mono2 text-[10px] tracking-[0.25em] uppercase transition-colors ${tab === t ? "bg-gold text-noir-bg" : "text-cream/50 hover:text-gold"}`} data-testid={`admin-tab-${t}`}>
              {t}
              {t === "inbox" && unread > 0 && (
                <span className={`ml-2 inline-flex items-center justify-center min-w-[16px] h-4 px-1 text-[9px] font-bold ${tab === "inbox" ? "bg-noir-bg text-gold" : "bg-gold text-noir-bg"}`} data-testid="inbox-unread-badge">{unread}</span>
              )}
            </button>
          ))}
        </div>

        {tab === "orders" && (
          <div className="mt-8 space-y-4" data-testid="admin-orders-list">
            {orders.length === 0 && <p className="text-cream/40 text-sm py-10 text-center border border-white/10">No orders yet.</p>}
            {orders.map((o) => (
              <div key={o.order_id} className="border border-white/10 p-6" data-testid={`admin-order-${o.order_id}`}>
                <div className="flex flex-wrap justify-between gap-3 mb-3 items-center">
                  <span className="font-mono2 text-sm text-gold">{o.order_id}</span>
                  <span className="text-sm text-cream/60">{o.email || "Guest"}</span>
                  <div className="flex items-center gap-3">
                    <span className={`font-mono2 text-[9px] tracking-[0.2em] uppercase px-2 py-1 border ${o.status === "shipped" ? "bg-cream text-noir-bg border-cream" : "bg-gold/10 border-gold/40 text-gold"}`}>
                      {o.status === "shipped" ? "Shipped" : "Paid"}
                    </span>
                    <span className="font-mono2 text-sm text-gold">{money(o.amount)}</span>
                  </div>
                </div>
                <p className="text-xs text-cream/50">
                  {o.items.map((i) => `${i.qty}× ${i.name} (${i.size})`).join(" · ")}
                  <span className="text-cream/30"> — {new Date(o.created_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</span>
                </p>
                {o.tracking_number && (
                  <p className="text-xs text-cream/50 mt-2" data-testid={`admin-tracking-${o.order_id}`}>
                    {o.carrier} — <span className="font-mono2 text-gold">{o.tracking_number}</span>
                  </p>
                )}
                {o.status !== "shipped" && (
                  shipOpen === o.order_id ? (
                    <ShipForm order={o} onDone={() => { setShipOpen(null); loadAll(); }} />
                  ) : (
                    <button onClick={() => setShipOpen(o.order_id)} className="mt-4 inline-flex items-center gap-2 border border-white/15 text-cream/60 px-4 py-2 font-mono2 text-[9px] tracking-[0.2em] uppercase hover:border-gold hover:text-gold transition-colors" data-testid={`ship-open-${o.order_id}`}>
                      <PackageCheck size={12} /> Mark Shipped
                    </button>
                  )
                )}
              </div>
            ))}
          </div>
        )}

        {tab === "products" && (
          <div className="mt-8" data-testid="admin-products-list">
            <button onClick={() => setEditing("new")} className="mb-6 inline-flex items-center gap-2 border border-gold/60 text-gold px-6 py-3 font-mono2 text-[10px] tracking-[0.25em] uppercase hover:bg-gold hover:text-noir-bg transition-colors" data-testid="admin-new-product-button">
              <Plus size={14} /> New Product
            </button>

            {editing && (
              <ProductForm
                initial={editing === "new" ? EMPTY : editing}
                images={images}
                isNew={editing === "new"}
                onSave={saveProduct}
                onCancel={() => setEditing(null)}
              />
            )}

            <div className="space-y-3">
              {products.map((p) => (
                <div key={p.slug} className="border border-white/10 p-4 flex flex-wrap items-center gap-4" data-testid={`admin-product-${p.slug}`}>
                  <div className="w-12 aspect-[3/4] bg-noir-card overflow-hidden shrink-0">
                    {p.images[0] && <img src={img(p.images[0])} alt="" className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1 min-w-[180px]">
                    <p className="font-display text-sm font-bold uppercase">{p.name}</p>
                    <p className="font-mono2 text-[10px] tracking-[0.2em] uppercase text-cream/40 mt-1">{p.serial} — {p.category} — {p.status.replace("_", " ")}</p>
                  </div>
                  <span className="font-mono2 text-sm text-gold">{money(p.price)}</span>
                  {(p.status === "sold_out" || p.status === "coming_soon") && (
                    <button onClick={() => restock(p.slug)} className="inline-flex items-center gap-2 border border-gold/50 text-gold px-4 py-2 font-mono2 text-[9px] tracking-[0.2em] uppercase hover:bg-gold hover:text-noir-bg transition-colors" data-testid={`admin-restock-${p.slug}`}>
                      <RotateCcw size={12} /> Restock & Alert
                    </button>
                  )}
                  <button onClick={() => setEditing(p)} className="inline-flex items-center gap-2 border border-white/15 text-cream/60 px-4 py-2 font-mono2 text-[9px] tracking-[0.2em] uppercase hover:border-gold hover:text-gold transition-colors" data-testid={`admin-edit-${p.slug}`}>
                    <Pencil size={12} /> Edit
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "inbox" && (
          <div className="mt-8 space-y-4" data-testid="admin-inbox-list">
            {messages.length === 0 && <p className="text-cream/40 text-sm py-10 text-center border border-white/10">No messages yet. Contact form messages land here.</p>}
            {messages.map((m) => (
              <MessageCard key={m.id} msg={m} onDone={loadAll} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
